import { useState, useEffect } from "react";
import { supabase } from "./db";
import Login from "./Login";
import Dashboard from "./Dashboard";
import ManualListing from "./ManualListing";
import AdminDashboard from "./AdminDashboard";
import MasterDashboard from "./MasterDashboard";
import ContentDashboard from "./ContentDashboard";
import SkuDashboard from "./SkuDashboard";
import SupplierProfile from "./SupplierProfile";
import SupplierProductList from "./SupplierProductList";
import "./styles.css";

// ⚠️ Definovanie rolí
const MASTER_EMAIL = "manager@1.sk";
const ADMIN_EMAILS = ["adriantran.tt@gmail.com", "admin1@1.sk"];
const CONTENT_EMAILS = ["content@1.sk", "web@1.sk"];
const CISELNIK_EMAILS = ["ciselnik@1.sk"];

export default function App() {
  const [session, setSession] = useState(null);
  const [view, setView] = useState("selection");

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 850);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => setSession(session));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) setView("selection");
    });
    return () => subscription.unsubscribe();
  }, []);

  if (!session) return <Login />;

  const userEmail = session.user.email?.trim().toLowerCase();

  // Zistenie rolí
  const isMaster = userEmail === MASTER_EMAIL.toLowerCase();
  const isAdmin = ADMIN_EMAILS.includes(userEmail);
  const isContent = CONTENT_EMAILS.includes(userEmail);
  const isCiselnik = CISELNIK_EMAILS.includes(userEmail);

  const isInternal = isMaster || isAdmin || isContent || isCiselnik;

  const s = getStyles(isInternal, isMobile);

  return (
    <div style={s.pageWrapper}>
      {/* 👇 ANIMÁCIE PRE SVETLÉ POZADIE */}
      <style>
        {`
          @keyframes floatLight1 {
            0% { transform: translate(0px, 0px) scale(1); }
            33% { transform: translate(40px, -60px) scale(1.1); }
            66% { transform: translate(-20px, 30px) scale(0.95); }
            100% { transform: translate(0px, 0px) scale(1); }
          }
          @keyframes floatLight2 {
            0% { transform: translate(0px, 0px) scale(1); }
            50% { transform: translate(-50px, 50px) scale(1.05); }
            100% { transform: translate(0px, 0px) scale(1); }
          }
          @keyframes floatLight3 {
            0% { transform: translate(0px, 0px) scale(1); }
            50% { transform: translate(60px, 20px) scale(1.1); }
            100% { transform: translate(0px, 0px) scale(1); }
          }
        `}
      </style>

      {/* PLÁVAJÚCE PASTELOVÉ BUBLINY */}
      <div style={s.blob1}></div>
      <div style={s.blob2}></div>
      <div style={s.blob3}></div>

      <div style={s.contentZIndex}>
        {/* =========================================
            HLAVIČKA (NAVBAR)
            ========================================= */}
        <div style={s.navbarContainer}>
          <div style={s.navbar}>
            <h1 style={s.navTitle}>
              {isMaster && <span>🌟 Senior Sales Dashboard</span>}
              {isAdmin && !isMaster && <span>📦 Sales Dashboard</span>}
              {isContent && <span>📝 Content Dashboard</span>}
              {isCiselnik && <span>🔢 Správa číselníka</span>}
              {!isInternal && <span>🛍️ Dodávateľský Dashboard</span>}
            </h1>
            
            <div style={s.navActions}>
              <div style={s.userInfo}>
                <div style={s.userAvatar}>
                  {userEmail.charAt(0).toUpperCase()}
                </div>
                <span style={s.userEmailText}>
                  {session.user.email}
                </span>
              </div>
              <button
                onClick={() => supabase.auth.signOut()}
                style={s.logoutBtn}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#fee2e2";
                  e.currentTarget.style.borderColor = "#fca5a5";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.6)";
                  e.currentTarget.style.borderColor = "rgba(226, 232, 240, 0.8)";
                }}
              >
                Odhlásiť sa
              </button>
            </div>
          </div>
        </div>

        {/* =========================================
            HLAVNÝ OBSAH
            ========================================= */}
        <div style={s.contentWrapper}>
          {/* Interné pohľady */}
          {isMaster && <MasterDashboard session={session} />}
          {isAdmin && !isMaster && <AdminDashboard session={session} />}
          {isContent && <ContentDashboard session={session} />}
          {isCiselnik && <SkuDashboard session={session} />}

          {/* Dodávateľský pohľad - PÔVODNÝ LAYOUT, NOVÝ DIZAJN */}
          {!isInternal && view === "selection" && (
            <div style={s.supplierLayout}>
              
              {/* 1. BLOK: Profil na vrchu */}
              <SupplierProfile session={session} />

              {/* 2. BLOK: Výber akcií (Vycentrované v strede) */}
              <div style={s.selectionCard}>
                <h2 style={s.headingCentered}>Ako chcete pridať nové produkty?</h2>
                <p style={s.subtitleCentered}>
                  Vyberte si spôsob, ktorý vám momentálne najviac vyhovuje.
                </p>

                <div style={s.actionCardsGrid}>
                  {/* Karta: Manuálny Listing */}
                  <div 
                    style={s.actionCard} 
                    onClick={() => setView("manual")}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = "#93c5fd";
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow = "0 15px 30px -10px rgba(59, 130, 246, 0.15)";
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.8)";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 10px 20px -10px rgba(0, 0, 0, 0.05)";
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.7)";
                    }}
                  >
                    <div style={s.cardHeader}>
                      <div style={s.iconWrapperBlue}>✍️</div>
                      <div style={s.arrowLinkBlue}>↗</div>
                    </div>
                    <h4 style={s.cardTitle}>Manuálny formulár</h4>
                    <p style={s.cardDesc}>Pridávanie po jednom. Ideálne pre menšie počty produktov.</p>
                  </div>

                  {/* Karta: Hromadný Import */}
                  <div 
                    style={s.actionCard} 
                    onClick={() => setView("bulk")}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = "#c4b5fd";
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow = "0 15px 30px -10px rgba(139, 92, 246, 0.15)";
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.8)";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 10px 20px -10px rgba(0, 0, 0, 0.05)";
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.7)";
                    }}
                  >
                    <div style={s.cardHeader}>
                      <div style={s.iconWrapperPurple}>📊</div>
                      <div style={s.arrowLinkPurple}>↗</div>
                    </div>
                    <h4 style={s.cardTitle}>Hromadný import</h4>
                    <p style={s.cardDesc}>Hromadné pridávanie položiek naraz pomocou Excel šablóny.</p>
                  </div>
                </div>
              </div>

              {/* 3. BLOK: História (Uzavretý rámček na celú šírku pod tým) */}
              <div style={s.historyContainer}>
                <div style={s.historyHeader}>
                  <h3 style={s.sectionTitle}>História zalistovaní</h3>
                  <span style={s.historySubtitle}>Prehľad vašich položiek</span>
                </div>
                <SupplierProductList session={session} />
              </div>

            </div>
          )}

          {/* Pohľady pre samotný listing */}
          {!isInternal && view === "manual" && (
            <ManualListing session={session} onBack={() => setView("selection")} />
          )}
          {!isInternal && view === "bulk" && (
            <Dashboard session={session} onBack={() => setView("selection")} />
          )}
        </div>
      </div>
    </div>
  );
}

// 🔥 ŠTÝLY PRE SVETLÝ "GLASSMORPHISM" & PÔVODNÝ LAYOUT
const getStyles = (isInternal, isMobile) => ({
  pageWrapper: {
    minHeight: "100vh",
    width: "100%",
    backgroundColor: "#f8fafc",
    fontFamily: "'Inter', sans-serif",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflowX: "hidden",
  },
  
  // -- Plávajúce pastelové bubliny --
  blob1: {
    position: "absolute",
    top: "-5%",
    left: "-5%",
    width: "500px",
    height: "500px",
    background: "radial-gradient(circle, rgba(254, 215, 170, 0.4) 0%, rgba(255,255,255,0) 70%)",
    borderRadius: "50%",
    zIndex: 1,
    filter: "blur(60px)",
    animation: "floatLight1 12s ease-in-out infinite",
  },
  blob2: {
    position: "absolute",
    bottom: "10%",
    right: "-5%",
    width: "600px",
    height: "600px",
    background: "radial-gradient(circle, rgba(186, 230, 253, 0.4) 0%, rgba(255,255,255,0) 70%)",
    borderRadius: "50%",
    zIndex: 1,
    filter: "blur(60px)",
    animation: "floatLight2 15s ease-in-out infinite alternate",
  },
  blob3: {
    position: "absolute",
    top: "30%",
    left: "40%",
    width: "450px",
    height: "450px",
    background: "radial-gradient(circle, rgba(233, 213, 255, 0.4) 0%, rgba(255,255,255,0) 70%)",
    borderRadius: "50%",
    zIndex: 1,
    filter: "blur(70px)",
    animation: "floatLight3 18s ease-in-out infinite",
  },

  contentZIndex: {
    position: "relative",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
  },

  // -- Navbar --
  navbarContainer: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    padding: "20px",
    display: "flex",
    justifyContent: "center",
  },
  navbar: {
    width: "100%",
    maxWidth: "1400px",
    backgroundColor: "rgba(255, 255, 255, 0.65)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: "20px",
    padding: "12px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: "1px solid rgba(255, 255, 255, 0.8)",
    boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.08)",
  },
  navTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "800",
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    letterSpacing: "-0.5px",
  },
  navActions: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "6px 12px",
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    borderRadius: "30px",
  },
  userAvatar: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #0f172a, #334155)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "bold",
  },
  userEmailText: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#334155",
    display: isMobile ? "none" : "block", // Skryje email na úzkych displejoch
  },
  logoutBtn: {
    padding: "8px 16px",
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    color: "#e11d48",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  contentWrapper: {
    width: "100%",
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "20px 20px 60px 20px",
    flex: 1,
  },
  
  // -- Dodávateľský Layout (Pôvodný pod sebou) --
  supplierLayout: {
    display: "flex",
    flexDirection: "column",
    gap: "30px", // Medzery medzi blokmi (Profil -> Výber -> História)
  },

  // -- Blok výberu (Vycentrovaný stred) --
  selectionCard: {
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    backdropFilter: "blur(20px)",
    borderRadius: "24px",
    padding: isMobile ? "30px 20px" : "40px",
    border: "1px solid rgba(255, 255, 255, 0.8)",
    boxShadow: "0 10px 30px -10px rgba(0, 0, 0, 0.05)",
  },
  headingCentered: {
    margin: "0 0 10px 0",
    fontSize: "26px",
    fontWeight: "800",
    color: "#0f172a",
    textAlign: "center",
  },
  subtitleCentered: {
    margin: "0 0 30px 0",
    fontSize: "15px",
    color: "#64748b",
    textAlign: "center",
  },

  // -- Zmenšené tlačidlá pre akcie vedľa seba --
  actionCardsGrid: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    justifyContent: "center",
    gap: "20px",
    maxWidth: "600px", // Obmedzuje ich šírku, aby neboli obrovské
    margin: "0 auto",
  },
  actionCard: {
    flex: 1, // Zabezpečí, že obe karty budú rovnako široké
    padding: "20px",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.8)",
    borderRadius: "20px",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 10px 20px -10px rgba(0, 0, 0, 0.05)",
  },
  cardHeader: {
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center",
    width: "100%", 
    marginBottom: "15px",
  },
  iconWrapperBlue: {
    width: "40px", 
    height: "40px",
    borderRadius: "12px",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    fontSize: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  iconWrapperPurple: {
    width: "40px",
    height: "40px",
    borderRadius: "12px",
    backgroundColor: "#f5f3ff",
    border: "1px solid #ddd6fe",
    fontSize: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  arrowLinkBlue: {
    fontSize: "18px",
    color: "#60a5fa",
    fontWeight: "bold",
  },
  arrowLinkPurple: {
    fontSize: "18px",
    color: "#c084fc",
    fontWeight: "bold",
  },
  cardTitle: {
    margin: "0 0 5px 0",
    fontSize: "16px",
    fontWeight: "700",
    color: "#0f172a",
  },
  cardDesc: {
    margin: 0,
    fontSize: "13px",
    color: "#64748b",
    lineHeight: "1.5",
  },

  // -- Uzavretý rámček pre Históriu --
  historyContainer: {
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    backdropFilter: "blur(20px)",
    borderRadius: "24px",
    padding: isMobile ? "20px" : "30px",
    border: "1px solid rgba(255, 255, 255, 0.8)", // Uzavretý rámček zo všetkých strán
    boxShadow: "0 10px 30px -10px rgba(0, 0, 0, 0.05)",
    overflowX: "auto", // Udržuje tabuľku vo vnútri
  },
  historyHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: "20px",
    borderBottom: "1px solid rgba(0,0,0,0.05)",
    paddingBottom: "15px",
  },
  sectionTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "800",
    color: "#0f172a",
  },
  historySubtitle: {
    fontSize: "14px",
    color: "#64748b",
    fontWeight: "500",
  }
});